<template>
	<v-col class="pa-0" cols="12">
		<v-card elevation="1" class="background-2 round-card">
			<v-card-title class="pa-3">
				<p :class="{'pl-3': $vuetify.breakpoint.smAndUp, 'pl-2': $vuetify.breakpoint.smAndDown}">Canale di invio</p>
				<v-spacer></v-spacer>
			</v-card-title>
			<v-divider></v-divider>

			<v-container class="pa-4" fluid>
				<v-select
					v-model="mailChannel"
					:items="channels"
					item-text="text"
					item-value="value"
					label="Canale di invio delle mail"
					outlined
					dense
					hide-details="auto"
					:menu-props="{'offset-y': true}"
				></v-select>

				<p class="text-caption mt-2 mb-0">
					Con il canale globale le mail escono dal server configurato per tutto il gestionale.
					Con l'SMTP personale escono dal server indicato qui sotto. Con un canale provider
					escono dall'account collegato e restano nella sua posta inviata.
				</p>
			</v-container>

			<v-divider></v-divider>

			<!-- Canale SMTP: form esistente, invariato -->
			<v-container v-if="mailChannel === 'smtp'" class="pa-0 pt-4" fluid>
				<fields-generator
					v-if="loaded"
					:fieldsset.sync="fieldsset"
					:model.sync="model"
					ref="fieldsgenerator"
				></fields-generator>
			</v-container>

			<!-- Canale globale: nessuna configurazione, e' il punto della voce -->
			<v-container v-else-if="mailChannel === 'global'" class="pa-4" fluid>
				<v-alert type="info" text dense class="mb-0">
					Le tue mail usano il server configurato per tutto il gestionale. Non c'e' niente da
					impostare qui.
				</v-alert>
			</v-container>

			<!-- Canale provider: scelta dell'account collegato -->
			<v-container v-else class="pa-4" fluid>
				<v-alert v-if="!providerAccounts.length" type="info" text dense>
					Nessun account collegato. Collegane uno per inviare da questo canale.
				</v-alert>

				<v-radio-group v-else v-model="mailAccountId" hide-details="auto" class="mt-0">
					<v-radio
						v-for="account in providerAccounts"
						:key="account.id"
						:value="account.id"
						:disabled="!account.usable"
					>
						<template v-slot:label>
							<span>{{ account.name }}</span>
							<v-chip v-if="account.motivo" x-small color="warning" class="ml-2">{{ etichettaMotivo(account.motivo) }}</v-chip>

							<!-- Le azioni stanno sulla riga dell'account, non in fondo:
								 prima per ricollegare lo stesso indirizzo bisognava capire
								 da soli che serviva "Collega un altro account".
								 Stanno dentro l'etichetta di una radio che, per un account
								 non utilizzabile, e' disabilitata: Vuetify le toglie i
								 pointer-events e i pulsanti morirebbero proprio nel caso in
								 cui servono. La classe azione-account li riattiva. -->
							<v-tooltip bottom v-if="account.motivo">
								<template v-slot:activator="{ on, attrs }">
									<v-btn v-bind="attrs" v-on="on" icon x-small class="ml-2 azione-account"
										:aria-label="account.motivo === 'permesso_invio_mancante' ? 'Concedi il permesso di invio' : 'Ricollega questo account'"
										:title="account.motivo === 'permesso_invio_mancante' ? 'Concedi il permesso di invio' : 'Ricollega questo account'"
										@click.stop.prevent="ricollegaAccount(account)">
										<v-icon x-small color="primary">mdi-refresh</v-icon>
									</v-btn>
								</template>
								<span>{{ account.motivo === 'permesso_invio_mancante' ? 'Concedi il permesso di invio' : 'Ricollega questo account' }}</span>
							</v-tooltip>

							<v-tooltip bottom v-if="mailAccountId === account.id">
								<template v-slot:activator="{ on, attrs }">
									<v-btn v-bind="attrs" v-on="on" icon x-small class="ml-1 azione-account"
										aria-label="Non usare piu' questo account per l'invio"
										title="Non usare piu' questo account per l'invio"
										@click.stop.prevent="nonUsarePerInvio()">
										<v-icon x-small color="red">mdi-link-off</v-icon>
									</v-btn>
								</template>
								<span>Non usare piu' questo account per l'invio</span>
							</v-tooltip>
						</template>
					</v-radio>
				</v-radio-group>

				<p v-if="spiegazioneMotivo" class="text-caption mt-2 mb-0 warning--text">{{ spiegazioneMotivo }}</p>

				<v-btn text color="primary" class="mt-3 pl-0" @click="connectAccount">
					<v-icon left>{{ iconaCanale }}</v-icon>
					{{ testoPulsanteCollega }}
				</v-btn>
			</v-container>

			<v-divider></v-divider>
			<v-card-actions class="pr-4">
				<v-spacer></v-spacer>
				<v-btn text color="primary" @click.native="saveSMTP">Salva</v-btn>
			</v-card-actions>
		</v-card>
	</v-col>
</template>

<script>

	import FormsMixin from '@/mixins/FormsMixin'

	export default {
		name: 'standard-smtp',
		mixins: [FormsMixin],
		props: {},
		data() {
			return {
				fieldsset: {},
				saved: false,
				loaded: false,
				channels: [],
				accounts: [],
				mailChannel: 'smtp',
				mailAccountId: null,
			}
		},

		computed: {
			providerAccounts() {
				return this.accounts.filter(account => account.provider === this.mailChannel)
			},

			// Un solo posto a decidere: 'smtp' e 'global' non hanno un account
			// collegato, tutti gli altri canali si'. Prima la regola era scritta
			// due volte con due formulazioni diverse, e l'arrivo di 'global' ha
			// fatto sbagliare entrambe.
			canaleUsaAccount() {
				return this.mailChannel !== 'smtp' && this.mailChannel !== 'global'
			},

			// Un account collegato dai Calendari non ha mai concesso gmail.send:
			// a quell'utente non serve riconnettere, serve dare un permesso che
			// non ha mai dato. Erano due casi diversi sotto la stessa etichetta.
			permessoInvioMancante() {
				return this.providerAccounts.some(account => account.motivo === 'permesso_invio_mancante')
			},

			nomeCanale() {
				return this.mailChannel === 'microsoft' ? 'Microsoft 365' : 'Google'
			},

			spiegazioneMotivo() {
				if (this.permessoInvioMancante) {
					// Su Google il caso tipico e' l'account collegato dai
					// Calendari, dove il consenso non comprende l'invio. Su
					// Microsoft quel percorso non esiste, e dirlo sarebbe falso.
					return this.mailChannel === 'microsoft'
						? 'Questo account e\' collegato ma non ha il permesso di inviare mail: ricollegalo qui sotto e concedi il permesso richiesto.'
						: 'Questo account e\' collegato ma non ha il permesso di inviare mail: e\' il caso di chi lo ha collegato dai Calendari. Concedilo qui sotto, il calendario continua a funzionare.'
				}

				if (this.providerAccounts.some(account => account.motivo === 'disconnesso')) {
					return 'Il collegamento con ' + this.nomeCanale + ' non e\' piu\' valido, di solito dopo un cambio password o una revoca. Ricollega l\'account qui sotto.'
				}

				return null
			},

			// Endpoint e icona dipendono dal canale scelto: prima erano cablati
			// su Google, quindi con un secondo provider il pannello avrebbe
			// proposto comunque un collegamento Google con l'icona sbagliata.
			endpointCollegamento() {
				return this.mailChannel === 'microsoft' ? 'microsoft/oauth' : 'google/oauth'
			},

			iconaCanale() {
				return this.mailChannel === 'microsoft' ? 'mdi-microsoft' : 'mdi-google'
			},

			// Il pulsante in fondo serve ad aggiungere un account, sempre.
			// Prima diventava "Concedi il permesso di invio" quando un account
			// ne aveva bisogno, e chi voleva collegarne un altro non aveva piu'
			// alcuna via: la riparazione del singolo account vive sulla sua
			// riga, dove si vede a chi si riferisce.
			testoPulsanteCollega() {
				return this.providerAccounts.length ? 'Collega un altro account' : 'Collega un account'
			}
		},

		methods: {

			etichettaMotivo(motivo) {
				return motivo === 'permesso_invio_mancante'
					? 'manca il permesso di invio'
					: 'da riconnettere'
			},

			// Stesso indirizzo, non uno nuovo: login_hint fa scegliere a Google
			// l'account giusto senza farlo cercare all'utente.
			ricollegaAccount(account) {
				this.avviaCollegamento({ login_hint: account.name })
			},

			// Smette di usarlo per l'invio senza toccare il collegamento
			// Google: il calendario e l'helpdesk continuano a funzionare. Si
			// torna al canale globale, che e' il default sensato.
			nonUsarePerInvio() {
				this.mailChannel = 'global'
				this.mailAccountId = null
				this.saveSMTP()
			},

			connectAccount() {
				this.avviaCollegamento()
			},

			// type=mail_send fa richiedere solo gli scope minimi (identita' +
			// gmail.send), niente calendar/gmail.readonly/gmail.modify: quelli
			// servono solo a Calendar/Helpdesk. origin=smtp fa tornare Google su
			// questa pagina (routes/web.php lo legge dallo "state" OAuth) invece
			// che sui calendari, e spa_origin fa tornare sul dominio da cui si e'
			// partiti invece che su quello configurato.
			avviaCollegamento(extra = {}) {
				// type e origin li interpreta solo il flusso Google; quello
				// Microsoft ha una sola destinazione e li ignorerebbe.
				const base = this.mailChannel === 'microsoft'
					? { spa_origin: window.location.origin }
					: { type: 'mail_send', origin: 'smtp', spa_origin: window.location.origin }

				const params = Object.assign(base, extra)

				this.axios.get(this.endpointCollegamento, { params }).then(response => {
					if (response.data.url) {
						window.location.href = response.data.url
						return
					}

					// Risposta buona ma senza URL: senza questo ramo il click non
					// produce nulla e sembra che il pulsante sia rotto.
					this.$store.commit('setSnackbar', {timeout: 6000, text: 'Impossibile avviare il collegamento: riprova.', value: true, color: 'error'})
				}, () => {
					this.$store.commit('setSnackbar', {timeout: 6000, text: 'Impossibile avviare il collegamento', value: true, color: 'error'})
				})
			},

			saveSMTP() {
				this.saved = true

				if (this.canaleUsaAccount && !this.mailAccountId) {
					this.saved = false
					this.$store.commit('setSnackbar', {timeout: 6000, text: 'Seleziona un account per questo canale prima di salvare', value: true, color: 'error'})
					return
				}

				// Il form dei campi SMTP esiste solo a scheda caricata (v-if="loaded"),
				// mentre il pulsante Salva e' sempre cliccabile: senza questa guardia
				// un click durante il caricamento solleva un errore e non succede
				// nulla. Non si passa dal ramo di validazione fallita, perche' qui
				// l'utente non ha sbagliato niente: deve solo aspettare.
				if (this.mailChannel === 'smtp' && !this.$refs.fieldsgenerator) {
					this.saved = false
					this.$store.commit('setSnackbar', {timeout: 6000, text: 'Attendi il caricamento della scheda prima di salvare', value: true, color: 'warning'})
					return
				}

				const validazione = this.mailChannel === 'smtp'
					? this.$refs.fieldsgenerator.$validator.validateAll()
					: Promise.resolve(true)

				validazione.then(result => {
					if (!result) {
						return this.handleValidationErrorAdvanced()
					}

					const payload = Object.assign({}, this.model, {
						mail_channel: this.mailChannel,
						mail_account_id: this.canaleUsaAccount ? this.mailAccountId : null,
					})

					this.axios.put('smtpuser', payload).then(response => {
						// get body data
						this.saved = false

						this.$store.commit('setSnackbar', {timeout: 6000, text: response.data.message, value: true, color: response.status === 200 ? 'success' : 'error'})

					}, response => {
						// error callback
						this.saved = false
					})
				}).catch((e) => {
					// console.log(e)
				})
			},

			// Carica model/fields/channels/accounts da api/smtpuser. Estratto in un
			// metodo a se' perche' va invocato anche dopo il ritorno dal consenso
			// OAuth (vedi created()), e mai in parallelo a quella chiamata: altrimenti
			// una GET smtpuser piu' lenta di quella di ritorno potrebbe sovrascrivere
			// lo stato con l'elenco account non ancora aggiornato.
			loadSmtpUser() {
				this.axios.get('smtpuser').then(response => {
					// get body data
					this.fieldsset = response.data.fields
					this.model = response.data.model
					this.channels = response.data.channels || []
					this.accounts = response.data.accounts || []
					this.mailChannel = response.data.model.mail_channel || 'smtp'
					this.mailAccountId = response.data.model.mail_account_id || null
					// Set defaultInput by back-end
					_.forEach(this.fieldsset.fields, (value, key) => {
						if(value.type === 'checkbox' || value.type === 'file') {
							Vue.set(this.model, value.key, '')
						}
						if(value.default) {
							Vue.set(this.model, value.key, value.default)
						}
					})

					_.forEach(this.defaultInput, (value, key) => {
						Vue.set(this.model, key, value)
					})

					this.$store.commit('endProgress')

					this.loaded = true

				}, response => {
						// error callback
				})
			}
		},

		created() {
			if (this.$route.query.code) {
				// Ritorno dal consenso OAuth: aspettiamo lo scambio del code prima di
				// caricare canale/account, altrimenti le due GET smtpuser (questa e
				// quella di loadSmtpUser chiamata da qui sotto) partirebbero in
				// parallelo e l'ultima risposta potrebbe vincere su quella corretta.
				// Il code di Google e' monouso: va tolto dall'indirizzo appena
				// consumato, altrimenti un aggiornamento della pagina ritenta lo
				// scambio e mostra un errore che non c'entra nulla.
				const code = this.$route.query.code

				// La rotta pubblica di ritorno aggiunge provider=microsoft: senza
				// leggerlo qui, il codice Microsoft finirebbe all'endpoint Google,
				// che lo rifiuterebbe con un errore incomprensibile.
				const endpoint = this.$route.query.provider === 'microsoft'
					? 'microsoft/oauth'
					: 'google/oauth'

				this.$router.replace({ query: {} }).catch(() => {})

				this.axios.get(endpoint, { params: { code: code } }).then(() => {
					this.$store.commit('setSnackbar', {timeout: 6000, text: 'Account collegato con successo', value: true, color: 'success'})
				}, () => {
					this.$store.commit('setSnackbar', {timeout: 6000, text: 'Collegamento account non riuscito', value: true, color: 'error'})
				}).finally(() => {
					this.loadSmtpUser()
				})
			} else {
				this.loadSmtpUser()
			}
		},
	}
</script>

<style scoped>
/* Una radio disabilitata ha pointer-events: none (Vuetify): i pulsanti
   nella sua etichetta devono restare cliccabili. */
.azione-account {
	pointer-events: auto;
}
</style>